/**
 * TournamentDrawDialog.jsx
 * Self-contained "Create Draw" dialog.
 * Manages its own draw state internally, calls onDrawCreated() on success.
 */
import { useState, useEffect } from 'react';
import { Trophy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export function TournamentDrawDialog({
  open, onClose, orgSlug, tournamentId, orgTeams, registeredTeamIds, onDrawCreated,
}) {
  const [seedOnly,       setSeedOnly]       = useState(true);
  const [groups,         setGroups]         = useState(2);
  const [teamsPerGroup,  setTeamsPerGroup]  = useState(4);
  const [day1,           setDay1]           = useState('2026-08-08');
  const [day2,           setDay2]           = useState('2026-08-09');
  const [teamOrder,      setTeamOrder]      = useState([]);
  const [creating,       setCreating]       = useState(false);
  const [drawResult,     setDrawResult]     = useState(null);

  // Initialise team order whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    setDrawResult(null);
    const registered = orgTeams.filter(t => registeredTeamIds?.has(t.id));
    const teams = registered.length > 0 ? registered : orgTeams;
    setTeamOrder(teams.map(t => t.id));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveTeam = (idx, dir) => {
    const next = [...teamOrder];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setTeamOrder(next);
  };

  const handleCreate = async () => {
    if (!seedOnly && teamOrder.length < 2) { toast.error('Need at least 2 teams'); return; }
    if (seedOnly && groups * teamsPerGroup < 2) { toast.error('Need at least 2 total teams'); return; }
    const totalTeams = seedOnly ? groups * teamsPerGroup : teamOrder.length;
    if (!confirm(`This will delete existing encounters and create a new draw with ${totalTeams} teams. Continue?`)) return;
    setCreating(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      const body  = seedOnly
        ? { groups, teams_per_group: teamsPerGroup, schedule_dates: [day1, day2].filter(Boolean) }
        : { team_ids: teamOrder,                    schedule_dates: [day1, day2].filter(Boolean) };
      const res  = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/create-draw`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        setDrawResult(data);
        toast.success(`Draw created! ${data.group_encounters} group matches + ${data.knockout_slots} semi/final slots`);
        onDrawCreated?.();
      } else {
        const msg = data.detail || 'Failed to create draw';
        toast.error(msg, { duration: 6000 });
        if (msg.toLowerCase().includes('format'))
          toast.info('Go to Format tab → add your sports first', { duration: 8000 });
      }
    } catch (_) { toast.error('Failed to create draw'); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) { onClose(); } }}>
      <DialogContent className="bg-[#161a26] border-[#252a37] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-draw-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[#f5c518]" />Create Tournament Draw
          </DialogTitle>
        </DialogHeader>

        {!drawResult ? (
          <div className="space-y-4">
            {/* Info */}
            <div className="bg-[#1d2230] rounded-lg p-3 text-xs text-[#8a93a3]">
              <p className="text-[#f0f2f7] font-medium mb-1">How the draw works</p>
              <p>Teams → <strong className="text-[#f5c518]">Group A</strong> (top half) + <strong className="text-[#f5c518]">Group B</strong> (bottom half) → round-robin → Semis → Final.</p>
              <p className="mt-1 text-[#f5c518]">Drag teams up/down to change their group assignment.</p>
            </div>

            {/* Mode toggle */}
            <div className="flex rounded-lg overflow-hidden border border-[#252a37]">
              <button onClick={() => setSeedOnly(false)}
                className={`flex-1 py-2 text-xs font-medium transition-colors ${!seedOnly ? 'bg-[rgba(245,197,24,0.12)] text-[#f5c518]' : 'bg-[#1d2230] text-[#8a93a3] hover:text-[#f0f2f7]'}`}>
                With Real Teams
              </button>
              <button onClick={() => setSeedOnly(true)}
                className={`flex-1 py-2 text-xs font-medium transition-colors ${seedOnly ? 'bg-[rgba(245,197,24,0.12)] text-[#f5c518]' : 'bg-[#1d2230] text-[#8a93a3] hover:text-[#f0f2f7]'}`}>
                Seed-Only (No Teams Yet)
              </button>
            </div>

            {/* Seed-only config */}
            {seedOnly && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#8a93a3] text-xs mb-1 block">Number of Groups</Label>
                    <Input type="number" min="1" max="8" value={groups}
                      onChange={e => setGroups(Math.min(8, Math.max(1, parseInt(e.target.value)||2)))}
                      className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] text-sm" />
                    <p className="text-[9px] text-[#8a93a3] mt-1">
                      Default: 2 groups (A + B). Increase if you need C, D etc.
                    </p>
                  </div>
                  <div>
                    <Label className="text-[#8a93a3] text-xs mb-1 block">Teams per Group</Label>
                    <Input type="number" min="2" max="8" value={teamsPerGroup}
                      onChange={e => setTeamsPerGroup(parseInt(e.target.value)||4)}
                      className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] text-sm" />
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Array.from({length:groups},(_,g) =>
                    Array.from({length:teamsPerGroup},(_,t) => {
                      const label = `${String.fromCharCode(65+g)}${t+1}`;
                      return <span key={label} className="text-xs px-2 py-1 rounded bg-[#1d2230] text-[#f5c518] border border-[rgba(245,197,24,0.3)] font-mono">{label}</span>;
                    })
                  )}
                </div>
                <div className="bg-[rgba(59,130,246,0.08)] border border-[rgba(59,130,246,0.25)] rounded-lg p-3 text-xs text-[#8a93a3]">
                  <p className="text-[#3b82f6] font-medium mb-1">Matches created with seed labels only</p>
                  <p>Assign real teams to seeds later via the <strong className="text-[#f0f2f7]">Groups tab → Assign Teams</strong>.</p>
                </div>
              </div>
            )}

            {/* Schedule dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#8a93a3] text-xs mb-1 block">📅 League Day</Label>
                <Input type="date" value={day1} onChange={e => setDay1(e.target.value)}
                  className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] text-sm" />
              </div>
              <div>
                <Label className="text-[#8a93a3] text-xs mb-1 block">🏆 Knockout Day</Label>
                <Input type="date" value={day2} onChange={e => setDay2(e.target.value)}
                  className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] text-sm" />
              </div>
            </div>

            {/* Real-team order */}
            {!seedOnly && (
              teamOrder.length === 0 ? (
                <p className="text-sm text-[#ef4444] text-center py-4">No teams found. Add teams first.</p>
              ) : (
                <div>
                  <p className="text-xs font-semibold text-[#8a93a3] uppercase tracking-wider mb-2">
                    Team Order ({teamOrder.length} teams)
                  </p>
                  {teamOrder.map((tid, idx) => {
                    const t = orgTeams.find(ot => ot.id === tid);
                    const groupLetter = idx < Math.ceil(teamOrder.length/2) ? 'A' : 'B';
                    const posInGroup  = idx < Math.ceil(teamOrder.length/2) ? idx+1 : idx-Math.ceil(teamOrder.length/2)+1;
                    return (
                      <div key={tid} className="flex items-center gap-3 py-2 px-3 bg-[#1d2230] rounded-lg mb-1.5">
                        <span className="text-xs font-bold px-2 py-0.5 rounded"
                          style={{ backgroundColor: groupLetter==='A'?'rgba(245,197,24,0.15)':'rgba(59,130,246,0.15)',
                                   color: groupLetter==='A'?'#f5c518':'#3b82f6' }}>
                          {groupLetter}{posInGroup}
                        </span>
                        <div className="h-7 w-7 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-xs text-white"
                          style={{ backgroundColor: t?.color||'#3b82f6' }}>
                          {t?.name?.[0]||'?'}
                        </div>
                        <span className="flex-1 text-sm text-[#f0f2f7] font-medium truncate">{t?.name||tid}</span>
                        <div className="flex gap-1">
                          <button onClick={() => moveTeam(idx,-1)} disabled={idx===0}
                            className="h-6 w-6 flex items-center justify-center text-[#8a93a3] hover:text-[#f0f2f7] disabled:opacity-30 text-xs">↑</button>
                          <button onClick={() => moveTeam(idx,1)} disabled={idx===teamOrder.length-1}
                            className="h-6 w-6 flex items-center justify-center text-[#8a93a3] hover:text-[#f0f2f7] disabled:opacity-30 text-xs">↓</button>
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-xs text-[#8a93a3] bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-lg p-2 mt-2">
                    ⚠️ This will <strong className="text-[#ef4444]">delete all existing encounters</strong> and generate a fresh draw.
                  </p>
                </div>
              )
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
              <Button onClick={handleCreate} disabled={creating||(!seedOnly&&teamOrder.length<2)}
                className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]" data-testid="confirm-create-draw-button">
                {creating ? 'Creating…' : seedOnly ? `Create ${groups}×${teamsPerGroup} Draw` : 'Create Draw'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* Draw success screen */
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="text-4xl mb-2">🏆</div>
              <p className="text-lg font-bold text-[#f5c518]">Draw Created!</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label:'Group Matches',   value:drawResult.group_encounters,  color:'#22c55e' },
                { label:'Semi + Final Slots',  value:drawResult.knockout_slots,    color:'#3b82f6' },
                { label:'Total Encounters',value:drawResult.total_encounters,  color:'#f5c518' },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-[#1d2230] rounded-lg p-3 text-center">
                  <p className="text-xl font-bold" style={{ color }}>{value}</p>
                  <p className="text-xs text-[#8a93a3]">{label}</p>
                </div>
              ))}
            </div>
            {drawResult.sport_match_totals && (
              <div>
                <p className="text-xs font-semibold text-[#8a93a3] uppercase tracking-wide mb-2">Group Stage Match Counts</p>
                <div className="space-y-1.5">
                  {Object.entries(drawResult.sport_match_totals).map(([sport, info]) => (
                    <div key={sport} className="flex items-center justify-between px-3 py-2 bg-[#1d2230] rounded-lg text-xs">
                      <span className="text-[#f0f2f7] capitalize">{sport.replace(/_/g,' ')}</span>
                      <span className="text-[#8a93a3]">
                        {info.matches_per_encounter} × {info.group_encounters} =
                        <span className="text-[#f5c518] font-bold ml-1">{info.group_matches}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={onClose} className="bg-[#f5c518] text-[#0b0f18]">View Matches →</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
